import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getUserData } from "../../../services/apiService";
import {
  fetchFaqAiChatHistory,
  fetchFaqAiChatSession,
} from "../../../services/faqService";
import { organizationCanSeeFaqTopic } from "../../../utils/orgModuleAccess";
import IsfPageTour from "../isf/IsfPageTour";
import "../isf/IsfPageTour.scss";
import { buildFaqAssistTourSteps } from "./faqAssistTourSteps";
import {
  hasCompletedFaqAssistTour,
  markFaqAssistTourCompleted,
} from "./faqAssistTourStorage";
import FaqAskPanel from "./FaqAskPanel";
import FaqAssistMark, { FaqAssistAiIcon } from "./FaqAssistMark";

function newSessionId() {
  return `ask-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function sessionTitle(session) {
  if (session?.title) {
    const text = String(session.title).trim().replace(/\s+/g, " ");
    return text.length > 48 ? `${text.slice(0, 48)}…` : text;
  }
  const firstUser = (session?.messages || []).find((m) => m.role === "user");
  if (firstUser?.text) {
    const text = String(firstUser.text).trim().replace(/\s+/g, " ");
    return text.length > 48 ? `${text.slice(0, 48)}…` : text;
  }
  if (session?.module) return `${session.module} chat`;
  return "New chat";
}

function formatSessionWhen(value) {
  const d = new Date(Number(value) || 0);
  if (Number.isNaN(d.getTime()) || !value) return "";
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((startToday - startThat) / 86400000);
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;
  const date = d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${date}, ${time}`;
}

function sessionMeta(session) {
  const moduleLabel = session?.module || "Chat";
  const when = formatSessionWhen(session?.updatedAt);
  return when ? `${moduleLabel} · ${when}` : moduleLabel;
}

const MODULE_ICONS = {
  QMS: "fas fa-file-alt",
  CAPA: "fas fa-clipboard-check",
  eISF: "fas fa-folder",
  "e-Source": "fas fa-file-signature",
  eSource: "fas fa-file-signature",
  Admin: "fas fa-wrench",
  CTMS: "fas fa-calendar-check",
  "My Inbox": "fas fa-bell",
  "Login & Access": "fas fa-key",
  "Study Management": "fas fa-gear",
};

/** Same accent keys / colours as FAQ Topics tree (`faq-tree-mod--*`). */
const MODULE_ACCENTS = {
  QMS: "qms",
  CAPA: "capa",
  eISF: "eisf",
  "e-Source": "esource",
  eSource: "esource",
  Admin: "admin",
  CTMS: "ctms",
  "My Inbox": "inbox",
  "Login & Access": "login",
  "Study Management": "study",
  "Platform Navigation": "platform",
};

function moduleIcon(name) {
  const key = String(name || "").trim();
  if (MODULE_ICONS[key]) return MODULE_ICONS[key];
  const lower = key.toLowerCase();
  if (lower.includes("qms")) return MODULE_ICONS.QMS;
  if (lower.includes("capa")) return MODULE_ICONS.CAPA;
  if (
    lower === "e-source" ||
    lower === "esource" ||
    lower.includes("e-source") ||
    lower.includes("esource")
  ) {
    return MODULE_ICONS["e-Source"];
  }
  if (lower.includes("eisf") || lower.includes("isf")) return MODULE_ICONS.eISF;
  if (lower.includes("admin")) return MODULE_ICONS.Admin;
  if (lower.includes("ctms") || lower.includes("eplanner") || lower.includes("e-planner"))
    return MODULE_ICONS.CTMS;
  if (lower.includes("inbox") || lower.includes("notification"))
    return MODULE_ICONS["My Inbox"];
  if (lower.includes("study")) return MODULE_ICONS["Study Management"];
  if (lower.includes("login") || lower.includes("access"))
    return MODULE_ICONS["Login & Access"];
  return "fas fa-cube";
}

function moduleAccent(name) {
  const key = String(name || "").trim();
  if (MODULE_ACCENTS[key]) return MODULE_ACCENTS[key];
  const lower = key.toLowerCase();
  if (lower.includes("qms")) return "qms";
  if (lower.includes("capa")) return "capa";
  if (
    lower === "e-source" ||
    lower === "esource" ||
    lower.includes("e-source") ||
    lower.includes("esource")
  ) {
    return "esource";
  }
  if (lower.includes("eisf") || lower.includes("isf")) return "eisf";
  if (lower.includes("admin")) return "admin";
  if (lower.includes("ctms") || lower.includes("eplanner") || lower.includes("e-planner"))
    return "ctms";
  if (lower.includes("inbox") || lower.includes("notification")) return "inbox";
  if (lower.includes("study")) return "study";
  if (lower.includes("platform") && lower.includes("navigation")) return "platform";
  if (lower.includes("login") || lower.includes("access")) return "login";
  return "default";
}

function moduleUsesAqLogo(name) {
  return moduleAccent(name) === "platform";
}

function feedbackFlagsFromMessages(messages) {
  const list = Array.isArray(messages) ? messages : [];
  let helpfulCount = 0;
  let notHelpfulCount = 0;
  list.forEach((m) => {
    if (m?.role !== "assistant") return;
    if (m.wasHelpful === true) helpfulCount += 1;
    if (m.wasHelpful === false) notHelpfulCount += 1;
  });
  return {
    helpfulCount,
    notHelpfulCount,
    hasHelpful: helpfulCount > 0,
    hasNotHelpful: notHelpfulCount > 0,
  };
}

function SessionFeedbackBadges({ hasHelpful, hasNotHelpful }) {
  if (!hasHelpful && !hasNotHelpful) return null;
  return (
    <span className="faq-ask-modal-rail-feedback">
      {hasHelpful ? (
        <span className="faq-ask-modal-rail-helpful" title="Marked helpful">
          <i className="fas fa-thumbs-up" aria-hidden="true" />
          <span className="visually-hidden">Helpful</span>
        </span>
      ) : null}
      {hasNotHelpful ? (
        <span
          className="faq-ask-modal-rail-not-helpful"
          title="Marked not helpful"
        >
          <i className="fas fa-thumbs-down" aria-hidden="true" />
          <span className="visually-hidden">Not helpful</span>
        </span>
      ) : null}
    </span>
  );
}

/**
 * Full-width AQ Assist workspace (modal).
 * 3 boxes: recent chats | chat thread | org FAQ modules.
 */
export default function FaqAskModal({
  open,
  onClose,
  fallbackModules = [],
  launchSession = null,
  onLaunchSessionHandled,
}) {
  const user = useMemo(() => getUserData() || {}, []);
  const staffId = user.StaffId || "";
  const organizationId = user.OrganizationId || user.Organizationid || "";

  const modules = useMemo(() => {
    const seen = new Set();
    const list = [];
    (fallbackModules || []).forEach((name) => {
      const trimmed = String(name || "").trim();
      const key = trimmed.toLowerCase();
      if (!trimmed || seen.has(key)) return;
      if (!organizationCanSeeFaqTopic(user, trimmed)) return;
      seen.add(key);
      list.push(trimmed);
    });
    return list;
  }, [fallbackModules, user]);

  const [sessions, setSessions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeId, setActiveId] = useState(() => newSessionId());
  const [panelKey, setPanelKey] = useState(0);
  const [seed, setSeed] = useState({ module: "", messages: [] });
  const [liveSession, setLiveSession] = useState({
    sessionId: "",
    module: "",
    messages: [],
    updatedAt: 0,
    title: "",
    hasHelpful: false,
    hasNotHelpful: false,
  });
  const [usage, setUsage] = useState({
    enabled: false,
    dailyOrgCap: 50,
    dailyUserCap: 50,
    orgRemaining: 0,
    userRemaining: 0,
  });
  const [showAssistTour, setShowAssistTour] = useState(false);
  const openRequestRef = useRef(0);
  const launchNonceRef = useRef(0);
  const wasOpenRef = useRef(false);
  const assistTourStartedRef = useRef(false);

  const refreshHistory = useCallback(async () => {
    if (!organizationId || !staffId) {
      setSessions([]);
      return [];
    }
    setHistoryLoading(true);
    try {
      const rows = await fetchFaqAiChatHistory(organizationId, staffId, 20);
      const mapped = (rows || []).map((row) => ({
        id: row.id,
        module: row.module,
        title: row.title,
        updatedAt: row.updatedAt,
        hasHelpful: !!row.hasHelpful,
        hasNotHelpful: !!row.hasNotHelpful,
        messages: [],
      }));
      setSessions(mapped);
      return mapped;
    } catch {
      setSessions([]);
      return [];
    } finally {
      setHistoryLoading(false);
    }
  }, [organizationId, staffId]);

  const startNewChat = useCallback(() => {
    openRequestRef.current += 1;
    const id = newSessionId();
    setActiveId(id);
    setSeed({ module: "", messages: [] });
    setLiveSession({
      sessionId: id,
      module: "",
      messages: [],
      updatedAt: 0,
      title: "",
      hasHelpful: false,
      hasNotHelpful: false,
    });
    setPanelKey((k) => k + 1);
  }, []);

  const openSession = useCallback(
    async (session) => {
      if (!session?.id) return false;

      const requestNo = ++openRequestRef.current;
      setActiveId(session.id);
      setLiveSession({
        sessionId: session.id,
        module: session.module || "",
        messages: [],
        updatedAt: Number(session.updatedAt) || 0,
        title: session.title || "",
        hasHelpful: !!session.hasHelpful,
        hasNotHelpful: !!session.hasNotHelpful,
      });

      try {
        const detail = await fetchFaqAiChatSession(
          organizationId,
          staffId,
          session.id,
        );
        if (requestNo !== openRequestRef.current) return false;

        const nextId = detail.id || session.id;
        const fromMessages = feedbackFlagsFromMessages(detail.messages);
        const hasHelpful = !!session.hasHelpful || fromMessages.hasHelpful;
        const hasNotHelpful =
          !!session.hasNotHelpful || fromMessages.hasNotHelpful;
        setActiveId(nextId);
        setSeed({
          module: detail.module || session.module || "",
          messages: detail.messages || [],
        });
        setLiveSession({
          sessionId: nextId,
          module: detail.module || session.module || "",
          messages: detail.messages || [],
          updatedAt: Number(detail.updatedAt) || Number(session.updatedAt) || 0,
          title: session.title || detail.messages?.[0]?.text || "",
          hasHelpful,
          hasNotHelpful,
        });
        if (hasHelpful || hasNotHelpful) {
          setSessions((prev) =>
            prev.map((row) =>
              row.id === nextId
                ? { ...row, hasHelpful, hasNotHelpful }
                : row,
            ),
          );
        }
        setPanelKey((k) => k + 1);
        return true;
      } catch {
        return false;
      }
    },
    [organizationId, staffId],
  );

  useEffect(() => {
    if (!open) {
      if (wasOpenRef.current) {
        startNewChat();
      }
      wasOpenRef.current = false;
      return undefined;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    let cancelled = false;
    const launchNonce = launchSession?.nonce || 0;
    const launchId = String(launchSession?.chatSessionId || "").trim();
    const launchTitle = String(launchSession?.title || "").trim();
    const launchModule = String(launchSession?.module || "").trim();
    const hasLaunch = !!(launchNonce || launchId || launchTitle);

    (async () => {
      const rows = await refreshHistory();
      if (cancelled) return;

      if (hasLaunch) {
        if (launchNonce && launchNonce === launchNonceRef.current) return;
        if (launchNonce) launchNonceRef.current = launchNonce;

        let match = null;
        if (launchId) {
          match = rows.find((row) => row.id === launchId) || {
            id: launchId,
            module: launchModule,
            title: launchTitle,
            updatedAt: Date.now(),
            hasHelpful: false,
            hasNotHelpful: false,
          };
        } else if (launchTitle) {
          const titleKey = launchTitle.toLowerCase();
          match =
            rows.find(
              (row) =>
                String(row.title || "")
                  .trim()
                  .toLowerCase() === titleKey,
            ) ||
            rows.find((row) =>
              String(row.title || "")
                .toLowerCase()
                .includes(titleKey.slice(0, 48)),
            ) ||
            null;
        }

        if (match?.id) {
          await openSession(match);
        }
        onLaunchSessionHandled?.();
        return;
      }

      // AQ Assist button / fresh open — never restore the previous chat.
      if (justOpened) {
        startNewChat();
      }
    })();

    // Only the header Close control dismisses AQ Assist (not backdrop / Escape).
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelled = true;
      document.body.style.overflow = prevOverflow;
    };
  }, [
    open,
    launchSession?.nonce,
    launchSession?.chatSessionId,
    launchSession?.title,
    launchSession?.module,
    refreshHistory,
    openSession,
    startNewChat,
    onLaunchSessionHandled,
  ]);

  const selectModule = useCallback(
    (moduleName) => {
      const next = String(moduleName || "").trim();
      if (!next) return;
      if (next === seed.module && (liveSession.messages || []).length === 0) {
        return;
      }
      openRequestRef.current += 1;
      const id = newSessionId();
      setActiveId(id);
      setSeed({ module: next, messages: [] });
      setLiveSession({
        sessionId: id,
        module: next,
        messages: [],
        updatedAt: 0,
        title: "",
        hasHelpful: false,
        hasNotHelpful: false,
      });
      setPanelKey((k) => k + 1);
    },
    [seed.module, liveSession.messages],
  );

  const handleSessionChange = useCallback(
    (snapshot) => {
      const nextMessages = snapshot?.messages || [];
      const nextModule = snapshot?.module || "";

      setLiveSession((prev) => {
        const prevMessages = prev.messages || [];
        const sameSnapshot =
          prev.sessionId === activeId &&
          prev.module === nextModule &&
          prevMessages.length === nextMessages.length &&
          JSON.stringify(prevMessages) === JSON.stringify(nextMessages);
        if (sameSnapshot) return prev;

        const prevUserCount = prevMessages.filter((m) => m.role === "user").length;
        const nextUserCount = nextMessages.filter((m) => m.role === "user").length;
        const userTurnAdded = nextUserCount > prevUserCount;
        const updatedAt = userTurnAdded
          ? Date.now()
          : Number(prev.updatedAt) || Date.now();
        const firstUser = nextMessages.find((m) => m.role === "user");
        const fromMessages = feedbackFlagsFromMessages(nextMessages);

        // Refresh Recent only after a new ask — not when hydrating an opened chat.
        if (userTurnAdded) {
          queueMicrotask(() => {
            refreshHistory();
          });
        }

        return {
          sessionId: activeId,
          module: nextModule,
          messages: nextMessages,
          updatedAt,
          title: firstUser?.text || prev.title || "",
          hasHelpful: !!prev.hasHelpful || fromMessages.hasHelpful,
          hasNotHelpful: !!prev.hasNotHelpful || fromMessages.hasNotHelpful,
        };
      });
    },
    [activeId, refreshHistory],
  );

  const handleUsageChange = useCallback((next) => {
    if (!next) return;
    setUsage({
      enabled: !!next.enabled,
      dailyOrgCap: Number(next.dailyOrgCap) || 0,
      dailyUserCap: Number(next.dailyUserCap) || 0,
      orgRemaining: Math.max(0, Number(next.orgRemaining) || 0),
      userRemaining: Math.max(0, Number(next.userRemaining) || 0),
    });
  }, []);

  const handleFeedbackChange = useCallback(
    ({ wasHelpful }) => {
      const helpful = !!wasHelpful;
      setSessions((prev) =>
        prev.map((session) =>
          session.id === activeId
            ? {
                ...session,
                hasHelpful: helpful ? true : !!session.hasHelpful,
                hasNotHelpful: helpful ? !!session.hasNotHelpful : true,
              }
            : session,
        ),
      );
      setLiveSession((prev) =>
        prev
          ? {
              ...prev,
              hasHelpful: helpful ? true : !!prev.hasHelpful,
              hasNotHelpful: helpful ? !!prev.hasNotHelpful : true,
            }
          : prev,
      );
    },
    [activeId],
  );

  const handleClose = useCallback(() => {
    setShowAssistTour(false);
    onClose?.();
  }, [onClose]);

  const showLiveInRail =
    open &&
    liveSession.sessionId === activeId &&
    (liveSession.messages || []).length > 0 &&
    !sessions.some((s) => s.id === activeId);

  useEffect(() => {
    if (!open) {
      assistTourStartedRef.current = false;
      setShowAssistTour(false);
      return undefined;
    }
    if (assistTourStartedRef.current) return undefined;
    if (hasCompletedFaqAssistTour(staffId)) return undefined;
    assistTourStartedRef.current = true;
    const timer = window.setTimeout(() => setShowAssistTour(true), 700);
    return () => window.clearTimeout(timer);
  }, [open, staffId]);

  const assistTourSteps = useMemo(
    () =>
      buildFaqAssistTourSteps({
        hasModules: modules.length > 0,
        hasRecentChats: sessions.length > 0 || !!showLiveInRail,
        hasUsage: !!usage.enabled,
        hasComposer: true,
      }),
    [modules.length, sessions.length, showLiveInRail, usage.enabled],
  );

  if (!open) return null;

  const activeModule = seed.module || liveSession.module || "";

  const liveFeedback = feedbackFlagsFromMessages(liveSession.messages);
  const feedbackTotals = (() => {
    let helpful = 0;
    let notHelpful = 0;
    const countedIds = new Set();
    sessions.forEach((session) => {
      countedIds.add(session.id);
      const isActiveLive =
        session.id === activeId &&
        liveSession.sessionId === session.id &&
        (liveSession.messages || []).length > 0;
      if (isActiveLive) {
        helpful += liveFeedback.helpfulCount;
        notHelpful += liveFeedback.notHelpfulCount;
        return;
      }
      helpful += session.hasHelpful ? 1 : 0;
      notHelpful += session.hasNotHelpful ? 1 : 0;
    });
    if (showLiveInRail && !countedIds.has(activeId)) {
      helpful += liveFeedback.helpfulCount;
      notHelpful += liveFeedback.notHelpfulCount;
    }
    return { helpful, notHelpful };
  })();

  const orgUsed = Math.max(0, usage.dailyOrgCap - usage.orgRemaining);
  const userUsed = Math.max(0, usage.dailyUserCap - usage.userRemaining);

  const modal = (
    <div
      className="faq-ask-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="faq-ask-modal-title"
    >
      <div
        className="faq-ask-modal-backdrop"
        aria-hidden="true"
      />
      <div className="faq-ask-modal-shell">
        <header className="faq-ask-modal-header">
          <div className="faq-ask-modal-header-main">
            <span className="faq-ask-modal-brand" aria-hidden="true">
              <FaqAssistMark bordered />
            </span>
            <div className="faq-ask-modal-header-copy">
              <h2 id="faq-ask-modal-title" className="faq-ask-modal-title">
                AQ Assist
                <FaqAssistAiIcon />
              </h2>
              <p className="faq-ask-modal-subtitle">
                AI-enabled how-to help for AQ Trials. Live responses in chat.
              </p>
            </div>
          </div>
          <div className="faq-ask-modal-header-actions">
            <button
              type="button"
              className="isf-tour-help-btn"
              data-tour="faq-assist-guide"
              title="Show AQ Assist guide"
              onClick={() => setShowAssistTour(true)}
            >
              <i className="fas fa-circle-question" aria-hidden="true" />
              Guide
            </button>
            <button
              type="button"
              className="btn-close"
              data-tour="faq-assist-close"
              onClick={handleClose}
              aria-label="Close"
            />
          </div>
        </header>

        <div className="faq-ask-modal-body">
          <aside className="faq-ask-modal-rail" aria-label="Chat history">
            <div className="faq-ask-modal-rail-toolbar">
              <div
                className="faq-ask-modal-rail-totals"
                data-tour="faq-assist-feedback-totals"
                title="Helpful / not helpful feedback in recent chats"
              >
                {feedbackTotals.helpful > 0 || feedbackTotals.notHelpful > 0 ? (
                  <>
                    {feedbackTotals.helpful > 0 ? (
                      <span className="faq-ask-modal-rail-helpful">
                        <i className="fas fa-thumbs-up" aria-hidden="true" />
                        <span className="faq-ask-modal-rail-feedback-count">
                          {feedbackTotals.helpful}
                        </span>
                      </span>
                    ) : null}
                    {feedbackTotals.notHelpful > 0 ? (
                      <span className="faq-ask-modal-rail-not-helpful">
                        <i className="fas fa-thumbs-down" aria-hidden="true" />
                        <span className="faq-ask-modal-rail-feedback-count">
                          {feedbackTotals.notHelpful}
                        </span>
                      </span>
                    ) : (
                      <span className="faq-ask-modal-rail-totals-empty">
                        Feedback totals
                      </span>
                    )}
                  </>
                ) : (
                  <span className="faq-ask-modal-rail-totals-empty">
                    Feedback totals
                  </span>
                )}
              </div>
              <button
                type="button"
                className="faq-ask-modal-new"
                data-tour="faq-assist-new-chat"
                onClick={startNewChat}
              >
                <i className="fas fa-plus" aria-hidden="true" />
                New chat
              </button>
            </div>

            <div
              className="faq-ask-modal-rail-scroll"
              data-tour="faq-assist-recent"
            >
              <div className="faq-ask-modal-rail-section">Recent</div>
              {historyLoading && sessions.length === 0 && !showLiveInRail ? (
                <p className="faq-ask-modal-rail-empty">Loading chat history…</p>
              ) : sessions.length === 0 && !showLiveInRail ? (
                <p className="faq-ask-modal-rail-empty">
                  Your recent chats will appear here after you send a question.
                </p>
              ) : (
                <ul className="faq-ask-modal-rail-list">
                  {showLiveInRail ? (
                    <li key={`live-${activeId}`}>
                      <button
                        type="button"
                        className="faq-ask-modal-rail-item is-active"
                      >
                        <span className="faq-ask-modal-rail-item-title-row">
                          <span className="faq-ask-modal-rail-item-title">
                            {sessionTitle(liveSession)}
                          </span>
                          <SessionFeedbackBadges
                            hasHelpful={
                              !!liveSession.hasHelpful ||
                              liveFeedback.hasHelpful
                            }
                            hasNotHelpful={
                              !!liveSession.hasNotHelpful ||
                              liveFeedback.hasNotHelpful
                            }
                          />
                        </span>
                        <span className="faq-ask-modal-rail-item-meta">
                          {sessionMeta(liveSession)}
                        </span>
                      </button>
                    </li>
                  ) : null}
                  {sessions.map((session) => {
                    const isActive = session.id === activeId;
                    const liveMatches =
                      liveSession.sessionId === session.id &&
                      (liveSession.messages || []).length > 0;
                    const liveFlags = feedbackFlagsFromMessages(
                      liveSession.messages,
                    );
                    const display =
                      isActive && liveMatches
                        ? {
                            ...session,
                            ...liveSession,
                            title:
                              liveSession.title ||
                              session.title ||
                              sessionTitle(session),
                            hasHelpful:
                              !!session.hasHelpful ||
                              !!liveSession.hasHelpful ||
                              liveFlags.hasHelpful,
                            hasNotHelpful:
                              !!session.hasNotHelpful ||
                              !!liveSession.hasNotHelpful ||
                              liveFlags.hasNotHelpful,
                          }
                        : {
                            ...session,
                            hasHelpful:
                              !!session.hasHelpful ||
                              (isActive &&
                                liveSession.sessionId === session.id &&
                                !!liveSession.hasHelpful),
                            hasNotHelpful:
                              !!session.hasNotHelpful ||
                              (isActive &&
                                liveSession.sessionId === session.id &&
                                !!liveSession.hasNotHelpful),
                          };
                    return (
                      <li key={session.id}>
                        <button
                          type="button"
                          className={`faq-ask-modal-rail-item${
                            isActive ? " is-active" : ""
                          }`}
                          onClick={() => openSession(session)}
                          aria-pressed={isActive}
                        >
                          <span className="faq-ask-modal-rail-item-title-row">
                            <span className="faq-ask-modal-rail-item-title">
                              {sessionTitle(display)}
                            </span>
                            <SessionFeedbackBadges
                              hasHelpful={!!display.hasHelpful}
                              hasNotHelpful={!!display.hasNotHelpful}
                            />
                          </span>
                          <span className="faq-ask-modal-rail-item-meta">
                            {sessionMeta({
                              module: display.module || session.module || "",
                              updatedAt:
                                Number(display.updatedAt) ||
                                Number(session.updatedAt) ||
                                0,
                            })}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </aside>

          <div className="faq-ask-modal-main" data-tour="faq-assist-thread">
            <FaqAskPanel
              key={panelKey}
              fallbackModules={modules}
              initialModule={seed.module}
              initialMessages={seed.messages}
              chatSessionId={activeId}
              onSessionChange={handleSessionChange}
              onUsageChange={handleUsageChange}
              onFeedbackChange={handleFeedbackChange}
              variant="modal"
              modulePicker="hidden"
            />
          </div>

          <aside
            className="faq-ask-modal-modules"
            aria-label="Modules"
            data-tour="faq-assist-modules"
          >
            <div className="faq-ask-modal-rail-section">Modules</div>
            {modules.length === 0 ? (
              <p className="faq-ask-modal-rail-empty">
                No FAQ modules are available for your organisation.
              </p>
            ) : (
              <ul className="faq-ask-modal-modules-list">
                {modules.map((name, index) => {
                  const isActive = activeModule === name;
                  const needsPickHint = !activeModule;
                  return (
                    <li key={name}>
                      <button
                        type="button"
                        className={`faq-ask-modal-module-item${
                          isActive ? " is-active" : ""
                        }${needsPickHint ? " is-pick-hint" : ""}`}
                        style={
                          needsPickHint
                            ? { animationDelay: `${index * 0.18}s` }
                            : undefined
                        }
                        onClick={() => selectModule(name)}
                        aria-pressed={isActive}
                      >
                        <span
                          className={`faq-ask-modal-module-icon faq-tree-mod--${moduleAccent(name)}`}
                          aria-hidden="true"
                        >
                          {moduleUsesAqLogo(name) ? (
                            <img
                              src="/logo.png"
                              alt=""
                              className="faq-ask-modal-aq-logo"
                            />
                          ) : (
                            <i className={moduleIcon(name)} />
                          )}
                        </span>
                        <span className="faq-ask-modal-module-label">
                          {name}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {usage.enabled ? (
              <div
                className="faq-ask-modal-usage"
                aria-label="Daily usage"
                data-tour="faq-assist-usage"
              >
                <div className="faq-ask-modal-rail-section">Today&apos;s usage</div>
                <div className="faq-ask-modal-usage-card">
                  <div className="faq-ask-modal-usage-row">
                    <span className="faq-ask-modal-usage-label">Organisation</span>
                    <span className="faq-ask-modal-usage-value">
                      {orgUsed} used · {usage.orgRemaining} left
                    </span>
                  </div>
                  <div className="faq-ask-modal-usage-bar" aria-hidden="true">
                    <span
                      style={{
                        width: `${
                          usage.dailyOrgCap > 0
                            ? Math.min(
                                100,
                                (orgUsed / usage.dailyOrgCap) * 100,
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <div className="faq-ask-modal-usage-row">
                    <span className="faq-ask-modal-usage-label">You</span>
                    <span className="faq-ask-modal-usage-value">
                      {userUsed} used · {usage.userRemaining} left
                    </span>
                  </div>
                  <div className="faq-ask-modal-usage-bar" aria-hidden="true">
                    <span
                      style={{
                        width: `${
                          usage.dailyUserCap > 0
                            ? Math.min(
                                100,
                                (userUsed / usage.dailyUserCap) * 100,
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </aside>
        </div>
      </div>
      <IsfPageTour
        open={showAssistTour}
        steps={assistTourSteps}
        staffId={staffId}
        markTourCompleted={markFaqAssistTourCompleted}
        onClose={() => setShowAssistTour(false)}
      />
    </div>
  );

  return createPortal(modal, document.body);
}
